import Vue from 'vue';
import Vuex from 'vuex';
import uuidv4 from 'uuid/v4';

Vue.use(Vuex);

// Helper functions

function createVideoObject(
  contentfulId = '',
  uuid = '',
  title = '',
  originalFilename = '',
  s3Key = '',
  publicUrl = '',
  thumbnailUrl = '',
  fileSize = 0,
) {
  return {
    contentfulId,
    uuid,
    title,
    originalFilename,
    s3Key,
    publicUrl,
    thumbnailUrl,
    fileSize,
    file: null,             // Only for VideoRow
    isSelected: false,      // Only for VideoTile
    isAlreadyInList: false, // Only for VideoTile
    createdAt: null         // Only for VideoTile
  };
}

function getContentfulEntryId() {
  const sys = Vue.prototype.$extension.entry.getSys();
  return sys.id;
}

function getTitle() {
  const titleField = Vue.prototype.$extension.entry.fields['title'];
  return titleField.getValue();
}

function getFieldValue() {
  console.log('======= this.$extension.field.getValue() =======');
  const value = Vue.prototype.$extension.field.getValue();
  console.log(value);
  return value;
}

function setFieldValue(value) {
  console.log('====== $extension.field.setValue() =======');
  Vue.prototype.$extension.field.setValue(value).then((data) => {
    console.log('Successfully set CF field', data);
  }).catch((error) => {
    console.log('error during saving field', error);
  });
}

// Used by VideoUploader in video mode
function getVideo() {
  const v = getFieldValue();
  if (v) {
    const cfId = getContentfulEntryId();
    const title = getTitle();
    const video = createVideoObject(
      cfId,
      v.uuid,
      title,
      v.originalFilename,
      v.s3Key,
      v.publicUrl,
      v.thumbnailUrl,
      v.fileSize
    );
    return [video];
  } else {
    return [];
  }
}

// Used by VideoPicker
async function getAllVideos() {
  const ITEMS_PER_PAGE = 1000;

  const ex = Vue.prototype.$extension;
  const idOfVideosInList = ex.parameters.invocation.idOfVideosInList;

  let videoList = [];
  let currentPage = 0;
  let totalPages = 1;

  try {
    // Perform pagination to get all Videos
    while (currentPage < totalPages) {
      const entries = await ex.space.getPublishedEntries({
        content_type: 'video',
        limit: ITEMS_PER_PAGE,
        skip: currentPage * ITEMS_PER_PAGE,
      });

      if (currentPage === 0) {
        totalPages = Math.ceil(Number(entries.total) / ITEMS_PER_PAGE);
      }

      const videos = entries.items
        .filter(cfVideo => {
          return cfVideo.fields.title && cfVideo.fields.video;
        })
        .map(cfVideo => {
          const vuexVideo = createVideoObject();
          copyContentfulVideoInfoToObject(vuexVideo, cfVideo);
          vuexVideo.isAlreadyInList = idOfVideosInList.includes(vuexVideo.uuid);
          return vuexVideo;
        });

      videoList = videoList.concat(videos);
      currentPage++;
    }
  } catch (err) {
    console.error('Error fetching all videos', err);
  }

  return videoList;
}

function copyContentfulVideoInfoToObject(video, cfEntry) {
  const cfId = cfEntry.sys.id;
  const title = cfEntry.fields.title['en-US'];
  const cfVideo = cfEntry.fields.video['en-US'];
  const createdAt = cfEntry.sys.createdAt;

  if (video) {
    video.contentfulId = cfId;
    video.title = title;
    video.originalFilename = cfVideo.originalFilename;
    video.s3Key = cfVideo.s3Key;
    video.publicUrl = cfVideo.publicUrl;
    video.thumbnailUrl = cfVideo.thumbnailUrl;
    video.fileSize = cfVideo.fileSize;
    video.uuid = cfVideo.uuid;
    video.createdAt = createdAt;
  }
};

function mapVuexVideoToContentfulVideo(vuexVideo) {
  return {
    uuid: vuexVideo.uuid,
    s3Key: vuexVideo.s3Key,
    fileSize: vuexVideo.fileSize,
    publicUrl: vuexVideo.publicUrl,
    thumbnailUrl: vuexVideo.thumbnailUrl,
    originalFilename: vuexVideo.originalFilename,
  };
}

function createVideoObjectAndPublish(video) {
  const ex = Vue.prototype.$extension;
  return ex.space.createEntry('video',
    {
      fields: {
        title: {
          'en-US': video.title,
        },
        video: {
          'en-US': mapVuexVideoToContentfulVideo(video)
        }
      }
    }
  ).then(entry => {
    return ex.space.publishEntry(entry);
  });
}

export default new Vuex.Store({
  state: {
    isReady: false,
    videos: [],
  },
  getters: {
    videoWithId: state => videoId => state.videos.find(v => v.uuid === videoId),
    selectedVideos: state => {
      return state.videos.filter(v => v.isSelected);
    },
    idOfVideos: state => {
      return state.videos.map(v => v.uuid);
    },
  },
  /* eslint-disable no-param-reassign */
  mutations: {
    setIsReady(state) {
      state.isReady = true;
      console.log('All the contents is ready in Vuex!', state);
    },
    setVideos(state, newVideos) {
      state.videos = newVideos;
    },
    addVideo(state, newFile) {
      const fileName = newFile.name;
      const newVideo = createVideoObject('', uuidv4(), fileName, fileName);
      newVideo.file = newFile;
      console.log('[mutation:addVideo]', newVideo);
      state.videos.push(newVideo);
    },
    appendExistingVideos(state, existingVideos) {
      state.videos = state.videos.concat(existingVideos);
    },
    updateVideoWithS3Info(state, newVideo) {
      const video = state.videos.find(v => v.uuid === newVideo.uuid);
      if (video) {
        video.file = null;
        video.title = newVideo.title;
        video.originalFilename = newVideo.originalFilename;
        video.s3Key = newVideo.s3Key;
        video.publicUrl = newVideo.publicUrl;
        video.thumbnailUrl = newVideo.thumbnailUrl;
        video.fileSize = newVideo.fileSize;
      }
    },
    updateVideoWithContentfulInfo(state, entry) {
      const cfId = entry.sys.id;
      const video = state.videos.find(v => v.contentfulId === cfId);
      copyContentfulVideoInfoToObject(video, entry);
    },
    removeVideo(state, videoId) {
      // Make sure the reference to local video file is cleared
      const video = state.videos.find(v => v.uuid === videoId);
      if (video) {
        video.file = null;
      }

      // Remove the video from the list
      state.videos = state.videos.filter(v => v.uuid !== videoId);
    },
    removeVideoWithContentfulId(state, contentfulId) {
      console.log('removeVideoWithContentulId:', contentfulId);
      state.videos = state.videos.filter(v => v.contentfulId !== contentfulId);
    },
    addContentfulIdToVideo(state, payload) {
      const video = state.videos.find(v => v.uuid === payload.uuid);
      if (video) {
        video.contentfulId = payload.contentfulId;
      }
    },
    flipSelection(state, uuid) {
      const video = state.videos.find(v => v.uuid === uuid);
      if (video) {
        video.isSelected = !video.isSelected;
      }
    },
  },
  /* eslint-enable no-param-reassign */
  actions: {
    // Used by VideoUploader in video mode
    loadSingleVideoForThisEntry({commit}) {
      const videos = getVideo(); // must be length of 0 or 1
      console.log('got video from a field, length:', videos.length);
      commit('setVideos', videos);
      commit('setIsReady');
    },
    // Used by VideoUploader in story mode
    loadVideosForThisStory({commit}) {
      // Fetch the list of video ids
      let value = getFieldValue();
      let videos = (value) ? value.map(v => {
        return createVideoObject(v.sys.id);
      }) : [];
      commit('setVideos', videos);

      // Variable to track removed videos
      const removedVideos = [];

      // Fetch video entry and fill in the state.videos
      const ex = Vue.prototype.$extension;
      const getEntryPromises = videos.map(v => {
        return ex.space.getEntry(v.contentfulId)
          .then((entry) => {
            commit('updateVideoWithContentfulInfo', entry);
            return entry.sys.id;
          }).catch(err => {
            if (err.code === 'NotFound') {
              console.log('Video entry is delete', v.contentfulId);
              commit('removeVideoWithContentfulId', v.contentfulId);
              removedVideos.push(v.contentfulId);
            } else {
              // Rethrow errors which are not NotFound
              throw(err);
            }
          });
      });

      // The data is ready when all of Videos are fetched
      Promise.all(getEntryPromises).then((result) => {
        commit('setIsReady');
        console.log('removedVideos', removedVideos);
        if (removedVideos.length > 0) {
          const multiple = removedVideos.length > 1;
          ex.dialogs.openAlert({
            title: multiple ? 'Deleted videos' : 'Deleted video',
            message: `It seems that somebody has manually deleted ${removedVideos.length} video${multiple ? 's' : ''} from the Contentful CMS, which ${multiple ? 'were' : 'was'} used in this story. Please re-upload the video${multiple ? 's' : ''} and publish if you would like to include ${multiple ? 'them' : 'it'} in this story.`,
            confirmLabel: 'OK'
          });
        }
      }).catch(err => {
        // This is for an unexpected error. NotFound is already handled above.
        console.error('Error happened while loading Videos', err);
      });
    },
    // Used by VideoPicker
    loadAllVideos({state, commit}) {
      getAllVideos().then(videos => {
        console.log('fetched all videos, length:', videos.length);
        commit('setVideos', videos);
        commit('setIsReady');
      });
    },
    createVideoEntryAndSaveVideoReferences({getters, commit, dispatch}, newVideo) {
      commit('updateVideoWithS3Info', newVideo);

      const fullNewVideo = getters.videoWithId(newVideo.uuid);
      createVideoObjectAndPublish(fullNewVideo).then((entry) => {
        commit('addContentfulIdToVideo', {
          uuid: fullNewVideo.uuid,
          contentfulId: entry.sys.id
        });
        dispatch('saveVideoReferencesToContentful');
      }).catch(err => {
        console.log('Error creating and publishing video: ', err);
      });
    },
    addExistingVideosAndSaveVideoReferences({commit, dispatch}, videos) {
      commit('appendExistingVideos', videos);
      dispatch('saveVideoReferencesToContentful');
    },
    saveVideoReferencesToContentful({state}) {
      // Use the existence of publicUrl and contentfulId as condition
      const completedVideos = state.videos.filter(v => {
        return v.publicUrl.length > 0 && v.contentfulId.length > 0;
      });

      // Copy data into pure old objects
      const listToSave = completedVideos.map(v => {
        return {sys: {id: v.contentfulId, linkType: 'Entry', type: 'Link'}};
      });

      setFieldValue(listToSave);
    },
    saveVideoEntry({getters, commit}, newVideo) {
      commit('updateVideoWithS3Info', newVideo);
      const videoToSave = mapVuexVideoToContentfulVideo(getters.videoWithId(newVideo.uuid));
      console.log('[saveVideoEntry] videoToSave:', videoToSave);
      setFieldValue(videoToSave);
    },
  },
});
